import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BridgeFiatRail } from 'services/merchants/types';
import { PayoutDestinationDetails, PayoutDestinationType } from 'services/payouts/types';

import { Button } from 'components/buttons';
import { RadioButton } from 'components/inputs/radio/radioButton';
import { RadioGroupItem, RadioGroupItemLabel } from 'components/inputs/radio/styles';
import { Buttons } from 'components/modal/styles';

import { CurrencyCode } from 'types/currency';

import { usePayout } from '../context';
import { usePayoutSetupStep } from '../context/modal';
import { Delimiter, Label, Section } from '../styles';
import { PayoutType } from '../types';
import LABELS from './keys';
import { GridLightText, PaymentRailOptionsContainer } from './styles';

interface FiatPayoutProps {
  payoutDestinations?: PayoutDestinationDetails[];
}

const FiatPayout = ({ payoutDestinations = [] }: FiatPayoutProps) => {
  const { t, i18n } = useTranslation();
  const payout = usePayout();
  const payoutStep = usePayoutSetupStep();

  const getPreselectedFiatPaymentRail = () => {
    const fiatPaymentRail = Object.values(BridgeFiatRail).find((rail) => payout.fiat.paymentRail === rail);

    return fiatPaymentRail;
  };

  const [paymentRail, setPaymentRail] = useState<BridgeFiatRail | undefined>(getPreselectedFiatPaymentRail());

  const paymentRailOptions = {
    [CurrencyCode.EUR]: [
      { key: BridgeFiatRail.SEPA, value: BridgeFiatRail.SEPA, label: t(LABELS.PAYMENT_RAIL_OPTIONS[BridgeFiatRail.SEPA]) },
    ],
    [CurrencyCode.MXN]: [
      { key: BridgeFiatRail.SPEI, value: BridgeFiatRail.SPEI, label: t(LABELS.PAYMENT_RAIL_OPTIONS[BridgeFiatRail.SPEI]) },
    ],
    [CurrencyCode.BRL]: [{ key: BridgeFiatRail.PIX, value: BridgeFiatRail.PIX, label: t(LABELS.PAYMENT_RAIL_OPTIONS[BridgeFiatRail.PIX]) }],
    [CurrencyCode.COP]: [
      { key: BridgeFiatRail.BRE_B, value: BridgeFiatRail.BRE_B, label: t(LABELS.PAYMENT_RAIL_OPTIONS[BridgeFiatRail.BRE_B]) },
    ],
    [CurrencyCode.USD]: [
      { key: BridgeFiatRail.ACH, value: BridgeFiatRail.ACH, label: t(LABELS.PAYMENT_RAIL_OPTIONS[BridgeFiatRail.ACH]) },
      {
        key: BridgeFiatRail.ACH_SAME_DATE,
        value: BridgeFiatRail.ACH_SAME_DATE,
        label: t(LABELS.PAYMENT_RAIL_OPTIONS[BridgeFiatRail.ACH_SAME_DATE]),
      },
      { key: BridgeFiatRail.WIRE, value: BridgeFiatRail.WIRE, label: t(LABELS.PAYMENT_RAIL_OPTIONS[BridgeFiatRail.WIRE]) },
      // TODO: this is temporary change to hide SWIFT option until we fix it
      ...(window.s4cConfig?.features?.swiftPayout
        ? [
            {
              key: BridgeFiatRail.SWIFT,
              value: BridgeFiatRail.SWIFT,
              label: t(LABELS.PAYMENT_RAIL_OPTIONS[BridgeFiatRail.SWIFT]),
            },
          ]
        : []),
    ],
  }[payout.merchant.mainFiatCurrency];

  const onPaymentRailSelect = (option: BridgeFiatRail) => () => {
    setPaymentRail(option);
  };

  const getPaymentRailFee = (option: BridgeFiatRail) => {
    const matchingDestination = payoutDestinations.find(
      (destination) =>
        destination.type === PayoutDestinationType.BANK_ACCOUNT &&
        destination.assets.some((a) => a === payout.merchant.mainFiatCurrency) &&
        destination.network === option,
    );

    const optionFee = matchingDestination?.fee ?? null;
    if (optionFee === null) {
      return null;
    }

    const formattedFee = new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: payout.merchant.mainFiatCurrency,
    }).format(optionFee.fixed);

    const formattedPercentage = optionFee.percent ? `${optionFee.percent}% + ` : '';

    return `${formattedPercentage}${formattedFee}`;
  };

  const onContinue = () => {
    if (!paymentRail) {
      throw new Error('Payment rail is not selected');
    }

    payout.setPayoutState({
      ...payout,
      [PayoutType.Fiat]: {
        ...payout[PayoutType.Fiat],
        paymentRail,
      },
    });

    payoutStep.proceed();
  };

  return (
    <>
      <Section>
        <Label>{t(LABELS.CURRENCY_LABEL)}</Label>
        {payout.merchant.mainFiatCurrency}
      </Section>
      <Section>
        <PaymentRailOptionsContainer>
          <Label>{t(LABELS.PAYMENT_RAIL)}</Label>
          <GridLightText>{t(LABELS.FEES)}</GridLightText>
          {paymentRailOptions.map((option) => (
            <Fragment key={option.key}>
              <RadioGroupItem key={option.key}>
                <RadioButton value={option.value === paymentRail} onChange={onPaymentRailSelect(option.value)} />
                <RadioGroupItemLabel>{option.label}</RadioGroupItemLabel>
              </RadioGroupItem>
              <GridLightText>{getPaymentRailFee(option.value)}</GridLightText>
            </Fragment>
          ))}
        </PaymentRailOptionsContainer>
      </Section>
      <Delimiter />
      <Buttons>
        <Button secondary onClick={payoutStep.close}>
          {t(LABELS.ACTIONS.CANCEL)}
        </Button>
        <Button primary disabled={!paymentRail} onClick={onContinue}>
          {t(LABELS.ACTIONS.CONTINUE)}
        </Button>
      </Buttons>
    </>
  );
};

export default FiatPayout;
